<script setup>
import { storeToRefs } from 'pinia'
import { ref, onMounted, watch, inject, computed } from 'vue'
import dayjs from 'dayjs'
import { useRouter } from 'vue-router'
import learningApiCourse from '@src/services/api/learning/course'
import learningApiUser from '@src/services/api/learning/user'
import { useLearningUserStore } from '@stores/user'
import Modal from '@components/modal/Modal.vue'
import { toast } from 'vue3-toastify'
import { commonUtil } from '@utils'
import { Core as YubinBangoCore } from 'yubinbango-core2'
import { PREF_OPTIONS } from '@enum/common.enum.js'

const router = useRouter()
const rootAppProvide = inject('rootAppProvide')
const { alreadyEntry, trainable } = storeToRefs(useLearningUserStore())

const props = defineProps({
  courseId: {
    type: String,
    required: true
  }
})
const course = ref(null)

const isButtonDisabled = ref(false)

const getCourse = () => {
  rootAppProvide.value.openContentsLoading()
  learningApiCourse.getCourseDetail({
    params: {
      with: 'location'
    },
    custom: {
      params: { courseId: props.courseId }
    }
  }).then((res) => {
    console.log('getCourseDetail', res)
    course.value = res.data
  }).catch((err) => {
    console.error(err)
  }).finally(() => {
    rootAppProvide.value.closeContentsLoading()
  })
}

const showCompleteModal = ref(false)

const toTop = () => {
  showCompleteModal.value = false
  router.push({
    name: 'Dashboard'
  })
}

// モーダル関連の状態
const showEntryConfirmModal = ref(false)
const showAddressInputModal = ref(false)
const deliveryAddress = ref({
  zipcode: '',
  pref: '',
  city: '',
  street: '',
  building: ''
})
const agree = ref(false)

// 初期の住所情報を保存
const initialAddress = ref(null)

// ユーザー情報の取得
const getUserInfo = async () => {
  try {
    const res = await learningApiUser.getUser()
    if (res.data.displays.office.address) {
      const address = {
        zipcode: res.data.displays.office.address.zipcode || '',
        pref: res.data.displays.office.address.pref || '',
        city: res.data.displays.office.address.city || '',
        street: res.data.displays.office.address.street || '',
        building: res.data.displays.office.address.building || ''
      }
      deliveryAddress.value = address
      initialAddress.value = { ...address } // 初期値を保存
    }
  } catch (err) {
    console.error('ユーザー情報の取得に失敗しました:', err)
  }
}

// 住所が変更されたかどうかを確認
const hasAddressChanged = () => {
  if (!initialAddress.value) return false
  return JSON.stringify(deliveryAddress.value) !== JSON.stringify(initialAddress.value)
}

// 申し込み処理
const entry = () => {
  showEntryConfirmModal.value = true
  isButtonDisabled.value = true
}

// 同意後の処理
const confirmEntry = () => {
  if (!agree.value) {
    toast('同意確認事項の一番下のチェックボックスをONにして同意してください（スマートフォンの場合はスクロールしてください）', {
      type: 'error',
      autoClose: 3000
    })
    return
  }
  showEntryConfirmModal.value = false
  if (course.value.besent) {
    showAddressInputModal.value = true
  } else {
    proceedEntry()
  }
}

// 送付先入力後の処理
const confirmAddress = () => {
  // 送付先住所のバリデーション
  if (!validateDeliveryAddress()) {
    return
  }
  
  showAddressInputModal.value = false
  proceedEntry()
}

// モーダルを閉じる処理
const closeEntryConfirmModal = () => {
  showEntryConfirmModal.value = false
  isButtonDisabled.value = false
}

const closeAddressInputModal = () => {
  showAddressInputModal.value = false
  isButtonDisabled.value = false
}

// 実際の申し込み処理
const proceedEntry = async () => {
  try {
    rootAppProvide.value.openContentsLoading()
    if (hasAddressChanged()) {
      const res = await learningApiUser.getUser()
      const user = res.data
      // 送信可能なプロパティのみを定義（AccountConfirm.vueのdisplays構造に基づく）
      const body = {
        displays: {
          name: user.displays.name,
          spell: user.displays.spell,
          birthday: user.displays.birthday,
          gender: user.displays.gender,
          address: {
            pref: user.displays.address?.pref
          },
          office: {
            name: user.displays.office?.name,
            section: user.displays.office?.section,
            address: {
              zipcode: deliveryAddress.value.zipcode,
              pref: deliveryAddress.value.pref,
              city: deliveryAddress.value.city,
              street: deliveryAddress.value.street,
              building: deliveryAddress.value.building || null
            },
            telephone: user.displays.office?.telephone,
            facsimile: user.displays.office?.facsimile,
            manager: user.displays.office?.manager
          }
        }
      }
      
      const updateRes = await learningApiUser.putUser(body)
      if (updateRes.data) {
        await postTrainee()
      }
    } else {
      await postTrainee()
    }
  } catch (err) {
    console.error('申し込みエラー:', err)
    if (err.response?.data?.error === 'user.displays.spell:missmatch') {
      toast('エラーが発生しました。名前（カナ）を確認してください。', {
        type: 'error'
      })
    } else {
      toast('エラーが発生しました。(error: ' + err.response?.data?.error + ')', {
        type: 'error'
      })
    }
  } finally {
    rootAppProvide.value.closeContentsLoading()
  }
}

// 申込API
const postTrainee = async () => {
  try {
    const body = { course_id: course.value.id }
    const res = await learningApiUser.postTrainee(body)
    if (res.data) {
      showAddressInputModal.value = false
      showCompleteModal.value = true
    }
  } catch (err) {
    console.error('postTraineeエラー:', err)
    toast('申込処理に失敗しました', { type: 'error' })
  }
}

// 郵便番号から住所を検索
const searchAddress = () => {
  return new YubinBangoCore(deliveryAddress.value.zipcode, (value) => {
    deliveryAddress.value.pref = value.region // 都道府県
    deliveryAddress.value.city = value.locality // 市区町村
    deliveryAddress.value.street = value.street // 町域
  })
}

// 郵便番号のフォーマット（ハイフンなし）
const formatZipcode = (value) => {
  return value.replace(/[^\d]/g, '').slice(0, 7)
}

// 郵便番号の入力監視
watch(() => deliveryAddress.value.zipcode, (newValue) => {
  deliveryAddress.value.zipcode = formatZipcode(newValue)
})

// 郵便番号のバリデーション
const validateZipcode = (zipcode) => {
  return zipcode && zipcode.match(/^\d{7}$/)
}

// 送付先住所のバリデーション
const validateDeliveryAddress = () => {
  const address = deliveryAddress.value
  
  // 郵便番号のチェック
  if (!validateZipcode(address.zipcode)) {
    toast('郵便番号は7桁の数字で入力してください。', {
      type: 'error'
    })
    return false
  }
  
  // 都道府県のチェック
  if (!address.pref || address.pref.trim() === '') {
    toast('都道府県を選択してください。', {
      type: 'error'
    })
    return false
  }
  
  // 市区町村のチェック
  if (!address.city || address.city.trim() === '') {
    toast('市区町村を入力してください。', {
      type: 'error'
    })
    return false
  }
  
  // 番地のチェック
  if (!address.street || address.street.trim() === '') {
    toast('番地を入力してください。', {
      type: 'error'
    })
    return false
  }
  
  return true
}

onMounted(async () => {
  console.log('courseDetail')
  getCourse()
  await getUserInfo()
})
</script>

<template>
  <div class="container">
    <h2>講座詳細</h2>
    <div
      v-if="course"
      class="courseDetail"
    >
      <table>
        <tr>
          <th>講座名称</th>
          <td>{{ course.displays.name }}</td>
        </tr>
        <tr>
          <th>講座タイプ</th>
          <td>{{ course.type === 'online' ? (course.besent ? 'オンデマンド＋DVD' : 'オンデマンド') : '会場' }}</td>
        </tr>
        <tr>
          <th>DVD送付</th>
          <td>{{ course.besent ? '有' : '無' }}</td>
        </tr>
        <tr v-if="course.type === 'real'">
          <th>会場名</th>
          <td>{{ course.location.displays.name }}</td>
        </tr>
        <tr v-if="course.type === 'real'">
          <th>会場住所</th>
          <td>〒{{ course.location.displays.address.zipcode }} {{ course.location.displays.address.pref }} {{ course.location.displays.address.city }} {{ course.location.displays.address.street }} {{ course.location.displays.address.building }}</td>
        </tr>
        <tr v-if="course.type === 'real'">
          <th>会場地図</th>
          <td>{{ course.location.latlong }}</td>
        </tr>
        <tr>
          <th>申込期間</th>
          <td>
            {{ dayjs(course.entryterm[0]).format('YYYY/MM/DD(dd) HH:mm') }}～
            {{ dayjs(course.entryterm[1]).format('YYYY/MM/DD(dd) HH:mm') }}
          </td>
        </tr>
        <tr>
          <th>開催期間</th>
          <td>
            {{ dayjs(course.term[0]).format('YYYY/MM/DD(dd) HH:mm') }}～
            {{ dayjs(course.term[1]).format('YYYY/MM/DD(dd) HH:mm') }}
          </td>
        </tr>
        <tr v-if="course.type === 'real'">
          <th>定員</th>
          <td>
            {{ course.location.displays.capacity }}
          </td>
        </tr>
        <tr v-else>
          <th>定員</th>
          <td>
            {{ course.displays.capacity }}
          </td>
        </tr>
        <tr>
          <th>受講料</th>
          <td>
            {{ course.price.toLocaleString() }}円
          </td>
        </tr>
        <tr>
          <th>ご案内</th>
          <td>
            {{ course.displays.guidance }}
          </td>
        </tr>
      </table>
      <div class="btnContainer">
        <button
          class="c-button c-button--medium c-button--border"
          @click="router.push({name: 'Dashboard'})"
        >
          一覧に戻る
        </button>
        <button
          v-if="trainable"
          class="c-button c-button--medium c-button--yellow"
          :disabled="((course.entryable && !alreadyEntry) || isButtonDisabled) ? null : 'disaqbled'"
          @click="entry"
        >
          申し込む
        </button>
      </div>
    </div>

    <Modal :show="showCompleteModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            講座の申込が完了しました。<br>
            受講料をお支払いください。
          </h2>
          <div class="l-modal__buttons">
            <button
              class="c-modalBtn c-modalBtn--single c-modalBtn--gray"
              type="button"
              @click="toTop"
            >
              トップページへ
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <!-- 申し込み確認モーダル -->
    <Modal :show="showEntryConfirmModal">
      <template #body>
        <h2 class="l-modal__ttl">
          同意確認事項（教育目的での使用に関する誓約を含む）
        </h2>
        <div class="l-modal__text">
          <p>本研修に参加するにあたり、以下の事項に同意・誓約いたします。</p>
          <ol>
            <li>1. 本研修で提供される資料、内容、ノウハウ、映像等は、すべて教育目的に限定して使用します。</li>
            <li>2. 営利目的、転売、二次配布、SNSや第三者への無断共有・流用等は一切行いません。</li>
            <li>3. 提供された情報・コンテンツを、事前の書面による許可なく複製・転載・商用利用しません。</li>
          </ol>
          <ul>
            <li>・上記の同意事項を遵守することを、修了証発行の条件とします</li>
            <li>・運営側が同意違反を確認した場合、事後であっても修了証の取り消し・無効措置を行う場合があります。</li>
          </ul>
          <!-- 同意のチェックボックス -->
          <div class="checkbox-container">
            <input
              type="checkbox"
              id="agree"
              v-model="agree"
            >
            <label for="agree">
              本同意事項に違反した場合、修了証の発行が無効となる可能性があることを
              理解し、了承します。
            </label>
          </div>
        </div>
        <div class="l-modal__buttons l-modal__buttons--twin">
          <button
            class="c-modalBtn c-modalBtn--twin c-modalBtn--black"
            type="button"
            @click="closeEntryConfirmModal"
          >
            キャンセル
          </button>
          <button
            class="c-modalBtn c-modalBtn--twin c-modalBtn--gray"
            type="button"
            @click="confirmEntry"
          >
            同意して申し込む
          </button>
        </div>
      </template>
    </Modal>

    <!-- 送付先入力モーダル -->
    <Modal :show="showAddressInputModal">
      <template #body>
        <h2 class="l-modal__ttl">
          DVDの送付先を確認してください。<br>
          現在翌録されている住所は以下となります。<br>
          送付先を変更される場合は修正してお申し込みください。
        </h2>
        <div class="l-modal__text">
          <div class="address-form">
            <div class="p-form-field zipcode">
              <label class="c-input-label"><span class="c-form-required">必須</span>郵便番号</label>
              <div class="zipcode-input-group">
                <input
                  v-model="deliveryAddress.zipcode"
                  type="text"
                  class="c-input-text"
                  placeholder="郵便番号"
                  autocomplete="off"
                  maxlength="7"
                >
                <button
                  class="zipCodeBtn"
                  @click.prevent="searchAddress()"
                >
                  検索
                </button>
              </div>
            </div>
            <div class="p-form-field">
              <label class="c-input-label"><span class="c-form-required">必須</span>都道府県</label>
              <select
                v-model="deliveryAddress.pref"
                class="c-input-select"
              >
                <option value="">都道府県</option>
                <option
                  v-for="(item, index) in PREF_OPTIONS"
                  :key="index"
                  :value="item.value"
                >
                  {{ item.name }}
                </option>
              </select>
            </div>
            <div class="p-form-field">
              <label class="c-input-label"><span class="c-form-required">必須</span>市区町村</label>
              <input
                v-model="deliveryAddress.city"
                type="text"
                class="c-input-text"
                placeholder="市区町村"
                autocomplete="off"
                maxlength="128"
              >
            </div>
            <div class="p-form-field">
              <label class="c-input-label"><span class="c-form-required">必須</span>番地</label>
              <input
                v-model="deliveryAddress.street"
                type="text"
                class="c-input-text"
                placeholder="番地"
                autocomplete="off"
                maxlength="128"
              >
            </div>
            <div class="p-form-field">
              <label class="c-input-label">建物名（ビル名）</label>
              <input
                v-model="deliveryAddress.building"
                type="text"
                class="c-input-text"
                placeholder="建物名（ビル名）"
                autocomplete="off"
                maxlength="256"
              >
            </div>
          </div>
        </div>
        <div class="l-modal__buttons l-modal__buttons--twin">
          <button
            class="c-modalBtn c-modalBtn--twin c-modalBtn--black"
            type="button"
            @click="closeAddressInputModal"
          >
            キャンセル
          </button>
          <button
            class="c-modalBtn c-modalBtn--twin c-modalBtn--gray"
            type="button"
            @click="confirmAddress"
          >
            申し込む
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style scoped lang="scss">
.container {
  h2 {
    padding: 30px 0 15px;
    font-size: 26px;
    text-align: left;
    font-weight: bold;
  }

  .courseDetail {
    width: 100%;

    table {
      width: 100%;
      border-top: 1px solid #ddd;

      th {
        width: 200px;
        height: 100%;
        padding: 8px 15px 10px;
        background: #eaeaea;
        border-bottom: 1px solid #ddd;
        font-size: 16px;
        text-align: right;
      }

      td {
        width: calc(100% - 200px);
        padding: 8px 15px 10px;
        border-bottom: 1px solid #ddd;
        border-right: 1px solid #ddd;
        font-size: 16px;

        button {
          display: inline-block;
          width: auto;
          padding: 5px 15px;
          margin-right: 10px;
          background: #ffbf00;
          color: #fff;
          text-align: center;
          font-weight: bold;
          font-size: 1.6rem;
          border-radius: 5px;
          text-decoration: none;
          vertical-align: middle;
        }
      }
    }
  }

  .btnContainer {
    display: flex;
    width: auto;
    margin: 20px auto;
    justify-content: center;

    button {
      width: 200px;

      &:nth-child(2) {
        margin-left: 20px;
      }
    }
  }
}

.address-form {
  padding: 20px;

  .form-group {
    margin-bottom: 15px;

    label {
      display: block;
      margin-bottom: 5px;
      font-weight: bold;
    }

    input {
      width: 100%;
      padding: 8px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 16px;

      &:focus {
        outline: none;
        border-color: #00008b;
      }

      &[readonly] {
        background-color: #f5f5f5;
      }
    }

    &.zipcode-group {
      .zipcode-input {
        display: flex;
        gap: 10px;

        input {
          flex: 1;
        }

        button {
          width: auto;
          padding: 8px 15px;
          white-space: nowrap;
        }
      }
    }
  }
}

.zipcode {
  .zipcode-input-group {
    display: flex;
    align-items: center;
    gap: 15px;
  }
  .c-input-text {
    width: 120px;
  }
  .zipCodeBtn {
    display: inline-block;
    width: 50px;
    height: 30px;
    border-radius: 4px;
    text-align: center;
    background: var(--corporate);
    color: #000;
  }
}

@media (max-width: 576px) {
  .container .courseDetail table {
    th {
      width: auto;
    }
  }
}
</style>
